import * as React from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './components/ui/tooltip.js';
import { useOpenOnTap } from './lib/use-open-on-tap.js';
import { cn } from './lib/utils.js';

export function DimeTooltipProvider({
  ...props
}: Readonly<React.ComponentProps<typeof TooltipProvider>>) {
  return <TooltipProvider {...props} />;
}

export function DimeTooltipRoot({ ...props }: Readonly<React.ComponentProps<typeof Tooltip>>) {
  return <Tooltip {...props} />;
}

export function DimeTooltipTrigger({
  ...props
}: Readonly<React.ComponentProps<typeof TooltipTrigger>>) {
  return <TooltipTrigger {...props} />;
}

export function DimeTooltipContent({
  ...props
}: Readonly<React.ComponentProps<typeof TooltipContent>>) {
  return <TooltipContent {...props} />;
}

/** The last selector hides the primitive's dark arrow, which a light card cannot wear. */
const CARD_CONTENT_CLASS =
  'flex-col items-stretch rounded-[10px] border border-border bg-popover px-3.5 py-3 text-left text-xs text-popover-foreground shadow-[0_10px_28px_rgba(0,0,0,0.14)] [&>span>svg.rotate-45]:invisible';

interface DimeTooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  delayDuration?: number;
  asChild?: boolean;
  /** Also opens on a touch or pen tap, since touch has no hover; only for a trigger with no click action of its own, as the tap is swallowed. */
  openOnTap?: boolean;
  /** Merged over the content's defaults, so it can override the `max-w-xs` cap. */
  contentClassName?: string;
  /** `card` is a light, bordered surface for rich content such as a table. */
  variant?: 'default' | 'card';
}

export function DimeTooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  delayDuration = 200,
  asChild = true,
  openOnTap = false,
  contentClassName,
  variant = 'default',
}: Readonly<DimeTooltipProps>) {
  const tap = useOpenOnTap();

  return (
    <DimeTooltipProvider delayDuration={delayDuration}>
      <DimeTooltipRoot {...(openOnTap ? { open: tap.open, onOpenChange: tap.onOpenChange } : {})}>
        <DimeTooltipTrigger asChild={asChild} {...(openOnTap ? tap.triggerProps : {})}>
          {children}
        </DimeTooltipTrigger>
        <DimeTooltipContent
          side={side}
          align={align}
          className={cn(variant === 'card' && CARD_CONTENT_CLASS, contentClassName)}
        >
          {content}
        </DimeTooltipContent>
      </DimeTooltipRoot>
    </DimeTooltipProvider>
  );
}
